"use client";

import { ArrowRight, Clock, Shield, ShieldCheck, Umbrella as UmbrellaIcon, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Authority() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const grid = useRevealOnScroll<HTMLDivElement>();
  const banner = useRevealOnScroll<HTMLDivElement>();
  const cta = useRevealOnScroll<HTMLDivElement>();
  const badges = [{
    icon: Shield,
    title: "Licensed & Certified",
    description: "Operating with full state environmental certifications to ensure your property meets all safety standards.",
    label: "State Certified"
  }, {
    icon: UserCheck,
    title: "Expert Technicians",
    description: "Our team brings 26 years of specialized experience to every inspection and testing project.",
    label: "26 Years Experience"
  }, {
    icon: Clock,
    title: "Rapid Response",
    description: "We prioritize your health and peace of mind with fast scheduling across Dade, Broward, and West Palm.",
    label: "Fast Turnaround"
  }, {
    icon: UmbrellaIcon,
    title: "Clinical Precision",
    description: "Evidence-forward testing protocols that provide clear, actionable data for your property.",
    label: "Data-Driven"
  }];
  const certifications = [{
    label: "Licensed & Insured"
  }, {
    label: "Certified Professionals"
  }, {
    label: "Environmental Experts"
  }];
  return <section data-section-id="673" id="certified-south-florida-authority" className="relative overflow-hidden bg-[hsl(223_83%_9%)] py-32 px-4 sm:px-6 lg:px-8">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-[80%] opacity-40" style={{
      background: "radial-gradient(ellipse 60% 70% at 50% 100%, hsla(208, 72%, 50%, 0.25), transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-7xl">
        {}
        <div ref={header.ref} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="text-sm font-medium tracking-[0.1em] text-[hsl(208_72%_50%)] uppercase font-default">
            Clinical Trust Standards
          </p>
          <h2 className="mt-4 text-4xl sm:text-5xl lg:text-[56px] lg:leading-[1.05] font-bold text-white font-default tracking-tighter">
            Evidence-forward testing for your peace of mind.
          </h2>
          <p className="mt-6 text-lg text-[hsl(215_16%_70%)] max-w-2xl mx-auto font-default">
            MYB Services delivers 26 years of certified expertise, providing the clinical precision required to identify and resolve environmental concerns.
          </p>
        </div>

        {}
        <div ref={grid.ref} className={`mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-700 ease-out ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {badges.map((badge, i) => {
          const Icon = badge.icon;
          return <Card data-index={i} key={i} className="bg-[hsl(223_83%_12%)] border border-[hsl(223_83%_20%)] rounded-lg p-8 min-h-[280px] flex flex-col items-center text-center shadow-lg hover:border-[hsl(208_72%_50%/0.4)] transition-colors duration-300">
                <div className="relative mb-6">
                  <div className="absolute inset-0 rounded-lg blur-xl opacity-40" style={{
                background: "hsl(208 72% 50% / 0.15)"
              }} aria-hidden="true" />
                  <div className="relative flex items-center justify-center w-[72px] h-[72px] rounded-lg border border-[hsl(223_83%_20%)] bg-[hsl(223_83%_9%)]">
                    <Icon className="w-9 h-9 text-[hsl(208_72%_50%)]" strokeWidth={1.5} />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white font-default mb-3">
                  {badge.title}
                </h3>
                <p className="text-sm text-[hsl(215_16%_70%)] font-default leading-relaxed mb-6 flex-1">
                  {badge.description}
                </p>
                <Badge variant="outline" className="bg-[hsl(208_72%_50%/0.1)] border-[hsl(208_72%_50%/0.3)] text-[hsl(203_93%_70%)] font-medium tracking-wide uppercase text-[10px] px-3 py-1 rounded-lg">
                  {badge.label}
                </Badge>
              </Card>;
        })}
        </div>

        {}
        <div ref={banner.ref} className={`mt-16 rounded-lg border border-[hsl(223_83%_20%)] bg-[hsl(223_83%_10%)] p-8 lg:p-12 transition-all duration-700 ease-out ${banner.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 lg:gap-6">
              {certifications.map((cert, i) => <div data-index={i} key={i} className="flex items-center gap-2 px-4 py-3 rounded-lg border border-[hsl(223_83%_20%)] bg-[hsl(223_83%_9%)]">
                  <ShieldCheck className="w-5 h-5 text-[hsl(203_93%_70%)]" strokeWidth={1.5} />
                  <span className="text-sm font-medium text-[hsl(215_16%_70%)] font-default tracking-wide">
                    {cert.label}
                  </span>
                </div>)}
            </div>

            {}
            <div className="text-center lg:text-right">
              <p className="text-2xl lg:text-3xl font-semibold text-white font-default leading-tight">
                Serving South Florida.{" "}
                <span className="text-[hsl(208_72%_50%)]">Certified Expertise.</span>
              </p>
            </div>
          </div>
        </div>

        {}
        <div ref={cta.ref} className={`mt-12 text-center transition-all duration-700 ease-out ${cta.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Button asChild variant="ghost" size="lg" className="text-[hsl(208_72%_50%)] hover:text-[hsl(203_93%_70%)] hover:bg-[hsl(208_72%_50%/0.1)] font-semibold text-base rounded-lg">
            <Link to="/ContactPage">
              Schedule your inspection
              <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}